import { memo } from 'react';

import {
  isAssetListComponent,
  isAssetSliderComponent,
  isContinueWatchingComponent,
  isSeasonSliderComponent,
} from '../../shared/helpers/componentHelpers';
import { useComponentAssets } from '../utils/useComponentAssets';

import { StructuredData } from './StructuredData';
import { getCategoryPageStructuredData } from './getCategoryPageStructuredData';

import type { ComponentId } from '@vgtv/api-client/lib/component';
import type { VFC } from 'react';
import type { CategoryPage } from '@vgtv/api-client/lib/page';
import type { SvpSeason } from '@vgtv/api-client/lib/svp_season';
import type { SvpSeasonSliderComponent } from '@vgtv/api-client/lib/component/slider';

type Props = {
  pageConfig: CategoryPage;
};

const getAssetListComponentFromSeasonsSlier = (
  slider: SvpSeasonSliderComponent
) => {
  const seasonNumber = getLatestSeasonNumber(slider.seasons);
  return (
    slider.components.find((c) => c.seasonNumber === seasonNumber) ??
    slider.components[0]!
  );
};

function getLatestSeasonNumber(seasons: SvpSeason[]) {
  return Math.max(...seasons.map((season) => season.seasonNumber));
}

const CategoryPageStructuredDataBase: VFC<Props> = ({ pageConfig }) => {
  const component = pageConfig.components
    .filter(
      (s) =>
        isAssetListComponent(s) ||
        isAssetSliderComponent(s) ||
        isSeasonSliderComponent(s)
    )
    .find((s) => !isContinueWatchingComponent(s));

  const hasSeasonSlider =
    component &&
    isSeasonSliderComponent(component) &&
    component.categoryId === pageConfig.category.id;
  const mainComponentWithAssets = hasSeasonSlider
    ? getAssetListComponentFromSeasonsSlier(component)
    : component;

  const assets = useComponentAssets(mainComponentWithAssets?.id as ComponentId);

  if (!component || !mainComponentWithAssets) {
    return null;
  }

  const data = getCategoryPageStructuredData({
    pageConfig,
    assets,
    categorySeasons: isSeasonSliderComponent(component) ? component : undefined,
  });

  return <StructuredData>{data}</StructuredData>;
};

export const CategoryPageStructuredData = memo(CategoryPageStructuredDataBase);
